/**
 * Tools to work with browser clipboards.
 * @module
 */
import type { UserNotificationType } from "common/types/userNotification";

// Includes fallback for older browsers
export default async function copyToClipboard(text: string): Promise<boolean> {
  if (typeof navigator !== "undefined" && navigator?.clipboard) {
    try {
      await navigator.clipboard.writeText(text);
    } catch (e) {
      console.error(e);
      return false;
    }
  } else {
    // create element, set text
    const el = document.createElement("textarea");
    el.value = text;

    // keep it hidden
    el.style.position = "fixed";
    el.style.zIndex = "-1";
    el.style.top = "0";
    el.style.left = "0";
    el.style.height = "0";
    el.style.opacity = ".01";

    // select it
    document.body.appendChild(el);
    el.focus();
    el.select();

    // copy to clipboard
    try {
      const copied = document.execCommand("copy");
      return !!copied;
    } catch (e) {
      console.error(e);
      return false;
    } finally {
      // remove element
      document.body.removeChild(el);
    }
  }

  return true;
}

export async function copyTextWithNotification(
  text: string,
  message: string = "Copied to clipboard!",
): Promise<UserNotificationType> {
  const successMsg: UserNotificationType = { messageType: "success", message };
  const errorMsg: UserNotificationType = { messageType: "error", message: "Failed to copy to clipboard." };

  const copied = await copyToClipboard(text);

  return copied ? successMsg : errorMsg;
}
